import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { Dropzone } from '../../components/Dropzone';
import { FileWithPreview } from '../../components/Dropzone/types';
import Button from '../../components/Forms/Button';
import FunnelModal from '../../components/Funnel/Modal/test';
import {
  PanelScrollableWrapper,
  PanelWrapper,
  PanelWrapperStyle,
} from '../../components/Funnel/Panel/common';
import { MainPanel } from '../../components/Funnel/Panel/MainPanel';
import { useAsync } from '../../hooks/useAsync';
import { ToastType, useToasts } from '../../hooks/useToasts';
import { MinusIcon } from '../../images/icons/minus';
import { PlusIcon } from '../../images/icons/plus';
import { WarningIcon } from '../../images/icons/warning';
import { AuthService } from '../../services/auth';
import { deleteFile, uploadSearchHeaderImage } from '../../services/files';
import { FileContext } from '../../services/files/types';
import SearchHeader, { ISearchHeaderProps } from '.';
import { searchHeaderMessages } from './messages';

const Container = styled.div`
  ${tw`relative`}
`;

const Actions = styled.div`
  ${tw`absolute flex flex-col`}
  top: var(--size-medium);
  right: var(--size-medium);
  gap: var(--size-medium);
`;

const ButtonStyle = css`
  ${tw`p-0 rounded-full flex justify-center items-center`}

  width:36px;
  height: 36px;
`;

type EditableSearchContentProps = ISearchHeaderProps & {
  initialImageUrl?: string;
  context: FileContext;
  metadata: string;
};

type ModalContext = {
  isUploadImageModalOpen: boolean;
  isDeleteImageModalOpen: boolean;
  isUploadImageModalHidden: boolean;
  fileToPreview: FileWithPreview | null;
  imageUrl?: string;
};

export const EditableSearchContent = (props: EditableSearchContentProps) => {
  const { context, initialImageUrl, metadata, ...rest } = props;
  const [searchHeaderContext, setSearchHeaderContext] = useState<ModalContext>({
    isUploadImageModalHidden: false,
    isUploadImageModalOpen: false,
    fileToPreview: null,
    imageUrl: initialImageUrl,
    isDeleteImageModalOpen: false,
  });
  const adminToken = AuthService.getAdminToken();

  return (
    <Container>
      <SearchHeader
        {...rest}
        imageUrl={
          searchHeaderContext.fileToPreview?.preview ||
          searchHeaderContext.imageUrl
        }
      />

      {adminToken && (
        <Actions>
          <Button
            css={ButtonStyle}
            onClick={() =>
              setSearchHeaderContext((currentContext) => ({
                ...currentContext,
                isUploadImageModalOpen: true,
                isUploadImageModalHidden: false,
              }))
            }
            variant="PRIMARY"
          >
            <PlusIcon />
          </Button>

          {searchHeaderContext.imageUrl && (
            <Button
              css={ButtonStyle}
              onClick={() =>
                setSearchHeaderContext((currentContext) => ({
                  ...currentContext,
                  isDeleteImageModalOpen: true,
                }))
              }
              variant="PRIMARY"
            >
              <MinusIcon />
            </Button>
          )}
        </Actions>
      )}

      {searchHeaderContext.isUploadImageModalOpen && (
        <div
          style={{
            display: searchHeaderContext.isUploadImageModalHidden
              ? 'none'
              : 'block',
          }}
        >
          <UploadImageModal
            fileToPreview={searchHeaderContext.fileToPreview}
            onClose={() =>
              setSearchHeaderContext((currentContext) => ({
                ...currentContext,
                isUploadImageModalOpen: false,
                fileToPreview: null,
              }))
            }
            revalidateImageUrl={(imageUrl) =>
              setSearchHeaderContext((currentContext) => ({
                ...currentContext,
                fileToPreview: null,
                imageUrl: imageUrl + `?${Date.now()}`,
              }))
            }
            setFileToPreview={(file) =>
              setSearchHeaderContext((currentContext) => ({
                ...currentContext,
                isUploadImageModalHidden: true,
                fileToPreview: file,
              }))
            }
            submit={(file) =>
              uploadSearchHeaderImage(
                {
                  image: file,
                  context,
                  metadata,
                },
                adminToken,
              )
            }
          />
        </div>
      )}

      {searchHeaderContext.isDeleteImageModalOpen && (
        <RemoveImageModal
          onClose={() => {
            setSearchHeaderContext((currentContext) => ({
              ...currentContext,
              isDeleteImageModalOpen: false,
            }));
          }}
          onSuccess={() =>
            setSearchHeaderContext((currentContext) => ({
              ...currentContext,
              isDeleteImageModalOpen: false,
              imageUrl: null,
            }))
          }
          submit={() =>
            deleteFile(
              {
                context,
                metadata,
              },
              adminToken,
            )
          }
        />
      )}
    </Container>
  );
};

const LeftButtonStyle = css`
  ${tw`px-0 whitespace-no-wrap`}
`;

const ToastStyle = css`
  ${tw`pb-small`}
  ${PanelWrapperStyle}
`;

const Wrapper = styled.div<{ centered?: boolean }>`
  ${tw`h-full overflow-hidden relative`}

  ${({ centered }) => centered && tw`flex justify-center items-center`}
`;

type UploadImageModalProps = {
  setFileToPreview: (file: FileWithPreview) => void;
  revalidateImageUrl: (imageUrl) => void;
  onClose: () => void;
  submit: (file: FileWithPreview) => ReturnType<typeof uploadSearchHeaderImage>;
  fileToPreview: FileWithPreview | null;
};

const UploadImageModal = (props: UploadImageModalProps) => {
  const {
    fileToPreview,
    onClose,
    revalidateImageUrl,
    setFileToPreview,
    submit,
  } = props;
  const [files, setFiles] = useState<FileWithPreview[]>(
    fileToPreview ? [fileToPreview] : [],
  );

  const selectedFile = files[0];

  const toastsQueue = useToasts(ToastStyle);

  const uploadImageContext = useAsync(() => submit(selectedFile), {
    manual: true,
    onSuccess: (response) => {
      revalidateImageUrl(response.url);

      toastsQueue.add(
        {
          type: ToastType.SUCCESS,
          message: (
            <FormattedMessage {...searchHeaderMessages.fileUploadSuccess} />
          ),
          duration: 5000,
        },
        { removeOthers: true },
      );
    },
    onError: (error) => {
      toastsQueue.add(
        {
          type: ToastType.ERROR,
          message: error.errorMessage || error.message || (
            <FormattedMessage {...searchHeaderMessages.unexpectedError} />
          ),
          duration: 5000,
        },
        { removeOthers: true },
      );
    },
  });

  const handleSetPreview = () => {
    if (selectedFile) {
      setFileToPreview(selectedFile);
    }
  };

  return (
    <FunnelModal mobileVariant="CENTERED">
      <MainPanel
        CTAButtonContent={<FormattedMessage {...searchHeaderMessages.save} />}
        CTALeftContent={
          <Button
            css={LeftButtonStyle}
            disabled={uploadImageContext.isLoading || !selectedFile}
            onClick={handleSetPreview}
            size="BIG"
            variant="TERCIARY"
          >
            <FormattedMessage {...searchHeaderMessages.preview} />
          </Button>
        }
        isCTAButtonDisabled={uploadImageContext.isLoading || !selectedFile}
        isCTAButtonLoading={uploadImageContext.isLoading}
        onCTAButtonClick={uploadImageContext.trigger}
        onClose={onClose}
        title={<FormattedMessage {...searchHeaderMessages.updateImage} />}
        withoutNodiImage
      >
        <Wrapper>
          <PanelScrollableWrapper>
            <Dropzone
              accept={{
                'image/*': [],
              }}
              files={files}
              maxFiles={1}
              maxSize={700 * 1024}
              onDropRejected={(rejectedFiles) => {
                if (rejectedFiles.length === 0) return;

                toastsQueue.add(
                  {
                    type: ToastType.ERROR,
                    message: rejectedFiles[0].errors[0].message,
                    duration: 5000,
                  },
                  { removeOthers: true },
                );
              }}
              setFiles={setFiles}
            />
          </PanelScrollableWrapper>
          {toastsQueue.ToastsComponent}
        </Wrapper>
      </MainPanel>
    </FunnelModal>
  );
};

const Content = styled.div`
  ${tw`flex flex-col items-center justify-center h-full`}
  gap:var(--size-medium);
`;

const IconWrapper = styled.span`
  ${tw`bg-toast-error rounded-full p-small flex items-center justify-center`}
  width: 50px;
  height: 50px;
`;

const TextWrapper = styled.div`
  ${tw`flex flex-col items-center justify-center text-center`}
  gap:var(--size-small)
`;

const TextTitle = styled.span`
  ${tw`text-title-xlarge leading-title-xlarge font-bold font-kyrial`}
`;

const Text = styled.span`
  ${tw`text-p-medium`}
`;

type RemoveImageModalProps = {
  onClose: () => void;
  onSuccess: () => void;
  submit: () => Promise<void>;
};

const RemoveImageModal = (props: RemoveImageModalProps) => {
  const { onClose, onSuccess, submit } = props;
  const toastsQueue = useToasts(ToastStyle);

  const removeImageContext = useAsync(submit, {
    manual: true,
    onSuccess,
    onError: (error) => {
      toastsQueue.add(
        {
          type: ToastType.ERROR,
          message: error.errorMessage || error.message || (
            <FormattedMessage {...searchHeaderMessages.unexpectedError} />
          ),
          duration: 5000,
        },
        { removeOthers: true },
      );
    },
  });

  return (
    <FunnelModal desktopVariant="SMALL" mobileVariant="SMALL">
      <MainPanel
        CTAButtonContent={
          <FormattedMessage {...searchHeaderMessages.yesDelete} />
        }
        CTALeftContent={
          <Button
            css={LeftButtonStyle}
            onClick={onClose}
            size="BIG"
            variant="TERCIARY"
          >
            <FormattedMessage {...searchHeaderMessages.noKeepIt} />
          </Button>
        }
        isCTAButtonDisabled={removeImageContext.isLoading}
        isCTAButtonLoading={removeImageContext.isLoading}
        onCTAButtonClick={removeImageContext.trigger}
        onClose={onClose}
        title={null}
        withoutHeader
        withoutNodiImage
      >
        <Wrapper centered>
          <PanelWrapper>
            <Content>
              <IconWrapper>
                <WarningIcon />
              </IconWrapper>

              <TextWrapper>
                <TextTitle>
                  <FormattedMessage {...searchHeaderMessages.deleteImage} />
                </TextTitle>

                <Text>
                  <FormattedMessage
                    {...searchHeaderMessages.deleteHeaderImageConfirmation}
                  />
                </Text>
              </TextWrapper>
            </Content>
          </PanelWrapper>

          {toastsQueue.ToastsComponent}
        </Wrapper>
      </MainPanel>
    </FunnelModal>
  );
};
